import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { X, Pencil } from 'lucide-react';
import { jiggle, poofExit } from './dockAnimations';
import { haptic } from '@/utils/haptics';
import type { DockApp } from './dockRegistry';

const ACTIVE_GRADIENT: [string, string] = ['#34d399', '#059669'];
const ACTIVE_GLOW = 'rgba(5, 150, 105, 0.4)';

interface DockItemProps {
  app: DockApp;
  editMode: boolean;
  onRemove: (id: string) => void;
  onEnterEdit?: () => void;
  onAction?: (action: string) => void;
  isMobile: boolean;
}

export function DockItem({ app, editMode, onRemove, onEnterEdit, onAction, isMobile }: DockItemProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const Icon = app.icon;
  const size = isMobile ? 72 : 80;
  const iconSize = isMobile ? 28 : 32;

  // Active when current path matches the dock item's route (ignore action-only items like quickReport)
  const isActive = !app.action && pathname.startsWith(app.to);
  const gradient = isActive ? ACTIVE_GRADIENT : app.gradient;
  const glow = isActive ? ACTIVE_GLOW : app.glow;

  const [showPencil, setShowPencil] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (isMobile || editMode) return;
    hoverTimer.current = setTimeout(() => setShowPencil(true), 500);
  }, [isMobile, editMode]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setShowPencil(false);
  }, []);

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.light();
    onRemove(app.id);
  };

  const handlePencilClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPencil(false);
    onEnterEdit?.();
  };

  const hasGradient = !!gradient;

  const content = (
    <motion.div
      className="relative flex flex-col items-center gap-1"
      style={{ width: isMobile ? 68 : 76 }}
      variants={jiggle}
      animate={editMode ? 'jiggle' : 'idle'}
      exit={poofExit}
      whileHover={!editMode ? { scale: 1.08, y: -4 } : undefined}
      whileTap={!editMode ? { scale: 0.95 } : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Liquid Glass icon — dock-icon-glass base + squircle + depth */}
      <div
        className="dock-icon-glass glass-entry-shimmer flex items-center justify-center"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          ...(hasGradient
            ? { background: `linear-gradient(180deg, ${gradient![0]}${isActive ? '30' : '0C'} 0%, ${gradient![1]}${isActive ? '40' : '12'} 100%)` }
            : {}),
          ...(isActive && glow ? { boxShadow: `0 0 20px ${glow}, 0 0 40px ${glow}` } : {}),
        }}
      >
        {/* Symbol — filled with asymmetric glass refraction */}
        {hasGradient ? (
          <div
            className="relative flex items-center justify-center"
            style={{
              width: iconSize,
              height: iconSize,
              transform: 'scaleX(1.12) scaleY(1.05)',
              zIndex: 2,
              filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.5)) drop-shadow(0 2px 6px rgba(0,0,0,0.2))',
              willChange: 'transform',
            }}
          >
            {/* Base icon — bright white fill */}
            <Icon
              size={iconSize}
              weight="fill"
              strokeWidth={1.5}
              style={{ color: 'rgba(255,255,255,0.9)' }}
            />
            {/* Diagonal highlight — upper-left brighter */}
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <Icon
                size={iconSize}
                weight="fill"
                strokeWidth={1.5}
                style={{
                  color: 'transparent',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.4) 0%, transparent 50%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                }}
              />
            </div>
            {/* Diagonal shadow — lower-right darker */}
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              style={{ mixBlendMode: 'multiply' }}
            >
              <Icon
                size={iconSize}
                weight="fill"
                strokeWidth={1.5}
                style={{
                  color: 'transparent',
                  background: 'linear-gradient(145deg, transparent 40%, rgba(0,0,0,0.3) 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                }}
              />
            </div>
          </div>
        ) : (
          <Icon
            size={iconSize}
            style={{ color: app.color, position: 'relative', zIndex: 2 }}
          />
        )}
      </div>

      {/* Remove badge (edit mode) */}
      {editMode && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 end-0 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 shadow-md"
          onClick={handleRemove}
          aria-label={t('home.dock.removeHint')}
        >
          <X size={12} className="text-white" strokeWidth={2.5} />
        </motion.button>
      )}

      {/* Pencil badge (delayed hover, desktop only) */}
      {!editMode && showPencil && (
        <motion.button
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute -top-1 end-0 flex h-6 w-6 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-md cursor-pointer border border-gray-200/60 hover:bg-white"
          onClick={handlePencilClick}
        >
          <Pencil size={11} className="text-gray-500" />
        </motion.button>
      )}

      {/* Label */}
      <span className="dock-label mt-1 select-none">
        {t(app.labelKey)}
      </span>
    </motion.div>
  );

  if (editMode) {
    return content;
  }

  if (app.action && onAction) {
    return (
      <button type="button" onClick={() => onAction(app.action!)} className="flex-shrink-0">
        {content}
      </button>
    );
  }

  return (
    <Link to={app.to} className="flex-shrink-0">
      {content}
    </Link>
  );
}
